// Zod-schema-to-fields walker. Walks a Zod schema and produces a recursive
// `FieldNode` tree consumed by the form wrapper to render a per-Block form.
//
// Per spec/tech-arch.md §7: the form generator reads a Block's
// `inputsSchema` (or `optionsSchema`) and produces a Preact form by walking
// the schema tree. The Zod-type → field-component mapping table:
//
// | Zod type                                | Field kind   | Component
// | --------------------------------------- | ------------ | -------------
// | z.string()                              | 'text'       | <TextField>      (P-025 — this phase)
// | z.string().describe('richText')         | 'richText'   | <RichTextField>  (P-028 — Tiptap)
// | z.string().url()                        | 'url'        | <UrlField>       (P-026)
// | z.string().url().describe('imageUrl')   | 'imageUrl'   | <ImageUrlField>  (P-026)
// | z.string().url().describe('derived')    | 'hidden'     | (nothing)        (P-037.24 D-132)
// | z.enum([...])                           | 'select'     | <SelectField>    (P-026)
// | z.array(...)                            | 'array'      | <ListField>      (P-027)
// | z.object({...})                         | 'object'     | <NestedField>    (P-027)
// | z.literal(...)                          | 'literal'    | (read-only)      (P-025)
// | z.union([...])                          | 'union'      | (placeholder)    (V1 placeholder; richer at P-027)
// | unknown                                 | 'unsupported'| (placeholder)
//
// **Derived brand** (P-037.24 / D-132). Any field marked with
// `.describe('derived')` is auto-generated by application code (e.g., a
// Canvas-composite pipeline) rather than staff-edited. The walker emits
// a `HiddenNode` (kind: `'hidden'`); the field renderer dispatches to a
// no-op renderer that returns `null`. The value still flows through the
// form's state — only the visible input is suppressed. Use case at
// introduction: `big3-signature-events.big3Events[].compositeImageUrl`.
//
// Wrappers (`ZodOptional`, `ZodNullable`, `ZodDefault`, `ZodEffects`) are
// transparently unwrapped; the inner schema's kind drives the field type.
// `ZodOptional` and `ZodNullable` mark the field as `required: false`.
// `ZodDefault` marks the field as `required: false` and captures the
// default value via `_def.defaultValue()`.
//
// Brand markers per Tech Arch §7 example:
//   const richText = (s: z.ZodString) => s.describe('richText')
//   const imageUrl = z.string().url().describe('imageUrl')
//
// The walker reads `_def.description` and dispatches on the literal string.
// `.describe(...)` is a compile-time string; no eval surface.
//
// Labels fall back to `humanize(name)` when no `.describe()` is set, OR
// when the description string is a brand marker (the brand string is not
// human-readable; the humanized field name is the right label).
//
// `helpText?` is reserved on every node for forward compat with a future
// "label / helpText" Zod convention; V1 leaves it undefined everywhere.
//
// Constraint extraction:
//   - ZodString: `min` / `max` from `_def.checks` (kind === 'min' | 'max')
//   - ZodArray:  `_def.minLength.value` / `_def.maxLength.value` /
//                `_def.exactLength.value`
//   - ZodEnum:   `_def.values` → `options`
//   - ZodLiteral: `_def.value`
//
// Known limitations at V1 (revisit at P-026 / P-027 / P-028):
//   - Top-level `z.union` produces `kind: 'union'` with variant FieldNodes
//     but the form wrapper renders a placeholder; paired-optional CTA UX
//     lands at P-027.
//   - ZodNumber / ZodBoolean / ZodDate / ZodBigInt produce
//     `kind: 'unsupported'`; no V1 Block schema uses them.
//   - ZodEffects (`.refine()`, `.transform()`) is unwrapped to its base
//     schema; refinement constraints are not surfaced as field-level
//     constraints (validation still fires on `safeParse`).

import type { z } from 'zod'

import { humanize } from './humanize'
import { zodDef } from './zod-introspect'

export type FieldNode =
  | TextNode
  | RichTextNode
  | UrlNode
  | ImageUrlNode
  | HiddenNode
  | SelectNode
  | ArrayNode
  | ObjectNode
  | LiteralNode
  | UnionNode
  | UnsupportedNode

interface BaseNode {
  readonly name: string
  readonly label: string
  readonly helpText?: string
  readonly required: boolean
  readonly defaultValue?: unknown
}

export interface TextNode extends BaseNode {
  readonly kind: 'text'
  readonly minLength?: number
  readonly maxLength?: number
}

export interface RichTextNode extends BaseNode {
  readonly kind: 'richText'
}

export interface UrlNode extends BaseNode {
  readonly kind: 'url'
}

export interface ImageUrlNode extends BaseNode {
  readonly kind: 'imageUrl'
}

/** P-037.24 D-132 — derived/auto-generated field. Renders nothing in
 *  the form; value flows through state untouched. */
export interface HiddenNode extends BaseNode {
  readonly kind: 'hidden'
}

export interface SelectNode extends BaseNode {
  readonly kind: 'select'
  readonly options: readonly string[]
}

export interface ArrayNode extends BaseNode {
  readonly kind: 'array'
  readonly element: FieldNode
  readonly minLength?: number
  readonly maxLength?: number
  readonly exactLength?: number
}

export interface ObjectNode extends BaseNode {
  readonly kind: 'object'
  readonly children: readonly FieldNode[]
}

export interface LiteralNode extends BaseNode {
  readonly kind: 'literal'
  readonly value: string | number | boolean | null
}

export interface UnionNode extends BaseNode {
  readonly kind: 'union'
  readonly variants: readonly FieldNode[]
}

export interface UnsupportedNode extends BaseNode {
  readonly kind: 'unsupported'
  readonly note: string
}

interface WalkOverrides {
  readonly required?: boolean
  readonly defaultValue?: unknown
  readonly description?: string
}

const BRAND_RICH_TEXT = 'richText'
const BRAND_IMAGE_URL = 'imageUrl'
const BRAND_DERIVED = 'derived'
const BRAND_STRINGS = [BRAND_RICH_TEXT, BRAND_IMAGE_URL, BRAND_DERIVED] as const
type BrandString = (typeof BRAND_STRINGS)[number]
function isBrandString(s: string | undefined): s is BrandString {
  return s !== undefined && (BRAND_STRINGS as readonly string[]).includes(s)
}

/**
 * Walk a Zod schema and produce a `FieldNode` tree.
 *
 * @param schema - The Zod schema to walk.
 * @param name - The field name (used for label fallback). Empty for the
 *               top-level walk on a Block's inputsSchema/optionsSchema.
 */
export function schemaToFields(schema: z.ZodTypeAny, name = ''): FieldNode {
  return walk(schema, name, {})
}

function walk(schema: z.ZodTypeAny, name: string, overrides: WalkOverrides): FieldNode {
  const def = zodDef(schema)
  const t: string = def.typeName

  if (t === 'ZodOptional' || t === 'ZodNullable') {
    return walk(def.innerType, name, { ...overrides, required: false })
  }
  if (t === 'ZodDefault') {
    return walk(def.innerType, name, {
      ...overrides,
      required: false,
      defaultValue: def.defaultValue(),
    })
  }
  if (t === 'ZodEffects') {
    return walk(def.schema, name, overrides)
  }

  const required = overrides.required ?? true
  const description =
    overrides.description ??
    (typeof def.description === 'string' ? (def.description as string) : undefined)
  const isBrand = isBrandString(description)
  const humanized = isBrand || description === undefined ? humanize(name) : description
  // The `imageUrl` brand drops a trailing " Url" so chooser-paradigm
  // fields (P-043 / D-129) read as "Image" / "Logo" / "Author Photo"
  // rather than "Image Url" / "Logo Url" / "Author Photo Url" — staff
  // thinks in images, not URLs. Plain `.url()` fields (websiteUrl,
  // ctaUrl) keep "Url" since URL IS the surfaced concept there.
  const label =
    description === BRAND_IMAGE_URL ? humanized.replace(/\s+Url$/, '') : humanized
  const base = baseNode(name, label, required, overrides.defaultValue)

  if (t === 'ZodString') {
    return stringNode(def, description, base)
  }
  if (t === 'ZodEnum') {
    return {
      ...base,
      kind: 'select',
      options: def.values as readonly string[],
    }
  }
  if (t === 'ZodArray') {
    return arrayNode(def, base)
  }
  if (t === 'ZodObject') {
    const shape = def.shape() as Record<string, z.ZodTypeAny>
    const children = Object.entries(shape).map(([childName, child]) => walk(child, childName, {}))
    return { ...base, kind: 'object', children }
  }
  if (t === 'ZodLiteral') {
    return { ...base, kind: 'literal', value: def.value as LiteralNode['value'] }
  }
  if (t === 'ZodUnion') {
    const variants = (def.options as readonly z.ZodTypeAny[]).map((v) => walk(v, '', {}))
    return { ...base, kind: 'union', variants }
  }

  return {
    ...base,
    kind: 'unsupported',
    note: `Unsupported Zod type: ${t}`,
  }
}

function baseNode(
  name: string,
  label: string,
  required: boolean,
  defaultValue: unknown
): BaseNode {
  // `exactOptionalPropertyTypes` (D-006): omit defaultValue when undefined
  // rather than assigning `defaultValue: undefined`.
  return defaultValue === undefined
    ? { name, label, required }
    : { name, label, required, defaultValue }
}

interface ZodCheck {
  readonly kind: string
  readonly value?: number
}

function stringNode(
  def: ReturnType<typeof zodDef>,
  description: string | undefined,
  base: BaseNode
): TextNode | RichTextNode | UrlNode | ImageUrlNode | HiddenNode {
  // ZodString always carries a `checks` array (empty when no constraints
  // were applied — verified against Zod 3.x source).
  const checks: readonly ZodCheck[] = def.checks as readonly ZodCheck[]
  const isUrl = checks.some((c) => c.kind === 'url')

  if (description === BRAND_RICH_TEXT) {
    return { ...base, kind: 'richText' }
  }
  if (description === BRAND_IMAGE_URL) {
    return { ...base, kind: 'imageUrl' }
  }
  if (description === BRAND_DERIVED) {
    return { ...base, kind: 'hidden' }
  }
  if (isUrl) {
    return { ...base, kind: 'url' }
  }

  const minLength = checks.find((c) => c.kind === 'min')?.value
  const maxLength = checks.find((c) => c.kind === 'max')?.value
  const text: TextNode = {
    ...base,
    kind: 'text',
    ...(minLength !== undefined ? { minLength } : {}),
    ...(maxLength !== undefined ? { maxLength } : {}),
  }
  return text
}

function arrayNode(def: ReturnType<typeof zodDef>, base: BaseNode): ArrayNode {
  const element = walk(def.type as z.ZodTypeAny, '', {})
  const minLength = (def.minLength as { value: number } | null | undefined)?.value
  const maxLength = (def.maxLength as { value: number } | null | undefined)?.value
  const exactLength = (def.exactLength as { value: number } | null | undefined)?.value
  return {
    ...base,
    kind: 'array',
    element,
    ...(minLength !== undefined ? { minLength } : {}),
    ...(maxLength !== undefined ? { maxLength } : {}),
    ...(exactLength !== undefined ? { exactLength } : {}),
  }
}
